/**
 * Point d'entrée pour les Web Components Lit des widgets Apidae
 *
 * Ce fichier expose les Web Components natifs pour être intégrés sur n'importe quel site web.
 * Les composants sont créés avec Lit, une bibliothèque légère pour créer des Web Components.
 *
 * Usage HTML simple (recommandé):
 * <script src="https://widgets.apidae-tourisme.com/apidae-widgets.js"></script>
 * <apidae-widget-liste widget-id="123"></apidae-widget-liste>
 * <apidae-widget-agenda widget-id="456"></apidae-widget-agenda>
 * <apidae-widget-carte widget-id="789"></apidae-widget-carte>
 * <apidae-widget-carrousel widget-id="101"></apidae-widget-carrousel>
 * <apidae-widget-liste-carte widget-id="102"></apidae-widget-liste-carte>
 * <apidae-widget-agenda-carte widget-id="103"></apidae-widget-agenda-carte>
 *
 * Le widget récupère automatiquement sa configuration depuis l'API:
 * - GET /api/v002/widgets/{widgetId} -> Configuration du widget
 * - GET /api/v002/referentiel/selections/ -> Données touristiques
 */

// Importer les composants communs
import "./components/Icon";
import "./components/WidgetHeader";
import "./components/WidgetLayout";
import "./components/Button";

// Importer tous les widgets
import "./widgets/ListWidget";
import "./widgets/AgendaWidget";
import "./widgets/MapWidget";
import "./widgets/CarouselWidget";
import "./widgets/ListMapWidget";
import "./widgets/AgendaMapWidget";

// Importer les types pour les rendre disponibles
export type {
  WidgetConfiguration,
  WidgetTemplateType,
} from "./types/widgets.d";
export type { ObjetTouristique } from "./types/apidae.d";
export type { WidgetBackend } from "./types/widgetBackend.d";

// Exposer une API JavaScript simple (optionnel, pour compatibilité)
interface WidgetAPI {
  /**
   * Crée et monte un widget dans un élément DOM
   * @param container - L'élément DOM ou son ID
   * @param widgetId - L'ID du widget à charger
   * @param widgetType - Le type de widget (liste, agenda, carte, carrousel, liste-carte, agenda-carte)
   * @param apiUrl - L'URL de base de l'API (optionnel)
   * @returns L'élément du widget créé
   */
  mount(
    container: HTMLElement | string,
    widgetId: string,
    widgetType?: string,
    apiUrl?: string,
  ): HTMLElement;

  /**
   * Démonte un widget
   * @param widget - L'élément du widget à démonter
   */
  unmount(widget: HTMLElement): void;

  /**
   * Version du package
   */
  version: string;
}

const ApidaeWidgets: WidgetAPI = {
  mount(
    container: HTMLElement | string,
    widgetId: string,
    widgetType: string = "liste",
    apiUrl?: string,
  ): HTMLElement {
    const targetElement =
      typeof container === "string"
        ? document.getElementById(container)
        : container;

    if (!targetElement) {
      throw new Error(`Container not found: ${container}`);
    }

    // Déterminer le nom du composant
    const componentName = `apidae-widget-${widgetType}`;

    // Créer le Web Component
    const widget = document.createElement(componentName);
    widget.setAttribute("widget-id", widgetId);
    if (apiUrl) {
      widget.setAttribute("api-url", apiUrl);
    }

    // Ajouter au DOM
    targetElement.appendChild(widget);

    return widget;
  },

  unmount(widget: HTMLElement): void {
    if (widget && widget.parentNode) {
      widget.parentNode.removeChild(widget);
    }
  },

  version: "1.0.0",
};

// Exposer l'API globalement
declare global {
  interface Window {
    ApidaeWidgets: WidgetAPI;
  }
}

window.ApidaeWidgets = ApidaeWidgets;

// Log de confirmation
console.log(
  `%c🎨 Apidae Widgets v${ApidaeWidgets.version} chargé avec succès`,
  "color: #168ac8; font-weight: bold; font-size: 14px;",
);

export default ApidaeWidgets;
